import React, { useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { createAppTheme } from '../Theme/Default';
import { createContextHardened } from '../Helper/ContextHelper';

interface Context {
  mode: PaletteMode;
  toggleMode: () => void;
  setMode: (mode: PaletteMode) => void;
}

const [ThemeModeContext, useThemeMode] = createContextHardened<Context>('ThemeMode');
export default useThemeMode;

const STORAGE_KEY = 'ESLMANAGER_THEME_MODE';

const readStoredMode = (): PaletteMode => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

interface Props {
  children: React.ReactNode;
}

export const ThemeModeProvider = ({ children }: Props): React.JSX.Element => {
  const [mode, setMode] = useState<PaletteMode>(readStoredMode);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Storage may be disabled (private mode, quota); preference just won't
      // persist across reloads, which is acceptable for a UI toggle.
    }
  }, [mode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider
      value={{
        mode,
        setMode,
        toggleMode: () => setMode((prev) => (prev === 'dark' ? 'light' : 'dark')),
      }}
    >
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
